import { useInView } from '@cian/valuation-utils-component';
import * as React from 'react';

import { useRecommendations } from './useRecommendations';
import { RecommendationSkeleton } from '../../components/RecommendationSkeleton';
import { EPaginationStatus } from '../../types/recommendation';
import { RecommendationsList } from '../../components/RecommendationsList';
import { Recommendation } from '../../components/Recommendation';
import { RecommendationFavoriteButtonContainer } from '../RecommendationFavoriteButtonContainer';
import { LabelsContainer } from '../LabelsContainer';
import { PaginationError } from '../../components/PaginationError';
import { ELoadingStatus } from '../../types/loadingStatus';
import { PaginationSentinel } from '../../components/PaginationSentinel';
import { RecentlyViewedContainer } from '../RecentlyViewedContainer';
import { RECENTLY_VIEWED_POSITION } from './constants';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../../utils/applicationContext';

export const RecommendationsListContainer: React.FC = () => {
  const {
    recommendations,
    hasMore,
    recommendationsStatus,
    paginationStatus,
    onLoad,
    onLoadMore,
    onRetry,
    onRecommendationClick,
    onShow,
    isOfferVisited,
  } = useRecommendations();

  const context = useApplicationContext();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  const recommendationsCountRef = React.useRef(recommendations.length);

  const recommendationsSkeleton = React.useMemo(
    () => new Array(8).fill(0).map((_, index) => <RecommendationSkeleton key={`recommendation-skeleton-${index}`} />),
    [],
  );

  React.useEffect(() => {
    if (inView && hasMore && paginationStatus === EPaginationStatus.Idle) {
      onLoadMore();
    }
  }, [hasMore, inView, onLoadMore, paginationStatus]);

  React.useEffect(() => {
    const previousRecommendationsCount = recommendationsCountRef.current;

    if (recommendations.length > 0) {
      if (previousRecommendationsCount === 0) {
        // Первая загрузка рекомендаций
        context.telemetry.increment(ETelemetryEvents.RecommendationsLoaded);
      } else if (recommendations.length > previousRecommendationsCount) {
        // Подгрузка дополнительных рекомендаций
        onShow();
      }
    }

    recommendationsCountRef.current = recommendations.length;
  }, [recommendations.length, onShow, context.telemetry]);

  if (recommendationsStatus === ELoadingStatus.Loading) {
    return <RecommendationsList onLoad={onLoad}>{recommendationsSkeleton}</RecommendationsList>;
  }

  if (!recommendations.length) {
    context.telemetry.increment(ETelemetryEvents.RecommendationsNotFound);

    return null;
  }

  return (
    <RecommendationsList onShow={onShow}>
      {recommendations.map((recommendation, index) => (
        <React.Fragment key={`recommendation-${index}`}>
          <Recommendation
            key={`recommendation-${index}`}
            onClick={onRecommendationClick}
            favoriteButton={<RecommendationFavoriteButtonContainer id={recommendation.offerId} position={index + 1} />}
            labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
            {...recommendation}
          />
          {index === RECENTLY_VIEWED_POSITION && <RecentlyViewedContainer />}
        </React.Fragment>
      ))}
      {paginationStatus === EPaginationStatus.Loading && recommendationsSkeleton}
      {paginationStatus === EPaginationStatus.Error && <PaginationError onClick={onRetry} />}
      {paginationStatus !== EPaginationStatus.Error && hasMore && <PaginationSentinel ref={ref} />}
    </RecommendationsList>
  );
};
